import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { Clock, TrendingUp, AlertTriangle, Play, MessageCircle, FileText, ClipboardCheck, Search, Plus, Trash2, Send, Filter, Download, Columns, MoreHorizontal, Volume2, X } from 'lucide-react';
import PatientHeader from './PatientHeader';
import VideoPlayer from './VideoPlayer';
import AdherenceChart from './AdherenceChart';
import Reconciliation from './Reconciliation';
import './Dashboard.css';
import '../pages/PatientOverview.css'; // Import PatientOverview styles for tabbed interface

const Dashboard = ({ patientId: propPatientId, onPatientChange }) => {
  const { patientId: urlPatientId } = useParams();
  const navigate = useNavigate();
  
  // Use prop patientId if provided (modal mode), otherwise use URL params
  const patientId = propPatientId || urlPatientId;
  
  // State for tabbed interface
  const [chartType, setChartType] = useState('doseTime'); // 'doseTime', 'adherence', 'videos', 'chats', 'notes', 'reconciliation'
  const [timeFilter, setTimeFilter] = useState('6months'); // '3months', '6months', '1year'
  const [selectedNoteId, setSelectedNoteId] = useState(1);
  const [notesSearchTerm, setNotesSearchTerm] = useState('');
  const [selectedChatId, setSelectedChatId] = useState(1);
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedMedications, setSelectedMedications] = useState({
    'Atorvastatin': true,
    'Metformin': true,
    'Lisinopril': true,
    'Sertraline': true,
    'Levothyroxine': true,
    'Advil': true,
    'Tylenol': true,
    'Aspirin': true,
    'Ibuprofen': true,
    'Amoxicillin': true
  });
  
  const selectedPatient = {
    id: parseInt(patientId) || 42,
    isOnline: patientId === '42' || patientId === '45' || patientId === '47' || patientId === '48',
    hasAlerts: patientId === '42' || patientId === '44' || patientId === '48'
  };

  const handlePatientChange = (newPatientId) => {
    if (onPatientChange) {
      // Modal mode - call the prop function
      onPatientChange(newPatientId);
    } else {
      // Regular mode - navigate to new patient page
      navigate(`/patient/${newPatientId}`);
    }
  };

  // Handler functions for tabbed interface
  const handleChartTypeChange = (type) => {
    setChartType(type);
  };

  const handleMedicationToggle = (medicationName) => {
    setSelectedMedications(prev => ({
      ...prev,
      [medicationName]: !prev[medicationName]
    }));
  };

  const getAdherenceColor = (adherence) => {
    if (adherence >= 80) return '#16a34a'; // Green
    if (adherence >= 60) return '#f59e0b'; // Orange  
    return '#dc2626'; // Red
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#16a34a';
      case 'offline': return '#6b7280';
      case 'not_assigned': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getMedicationColor = (medication) => {
    const colors = {
      'Atorvastatin': '#8B5CF6',
      'Metformin': '#EF4444',
      'Lisinopril': '#8B5CF6',
      'Sertraline': '#F59E0B',
      'Levothyroxine': '#EF4444',
      'Advil': '#3B82F6',
      'Tylenol': '#10B981',
      'Aspirin': '#8B5CF6',
      'Ibuprofen': '#06B6D4',
      'Amoxicillin': '#8B5CF6'
    };
    return colors[medication] || '#6b7280';
  };

  const getMedicationPercentage = (medication) => {
    const percentages = {
      'Atorvastatin': 78,
      'Metformin': 74,
      'Lisinopril': 82,
      'Sertraline': 71,
      'Levothyroxine': 89,
      'Advil': 68,
      'Tylenol': 85,
      'Aspirin': 73,
      'Ibuprofen': 77,
      'Amoxicillin': 91
    };
    return percentages[medication] || 75;
  };


  // Mock data for the dashboard - varies by patient
  const getMedicationData = (patientId) => {
    const baseData = [
      { name: 'Atorvastatin', percentage: 78, color: '#4F46E5' },
      { name: 'Metformin', percentage: 74, color: '#059669' },
      { name: 'Gabapentin', percentage: 60, color: '#DC2626' },
      { name: 'Omeprazole', percentage: 100, color: '#D97706' },
      { name: 'Levothyroxine', percentage: 78, color: '#7C2D12' },
      { name: 'Lisinopril', percentage: 0, color: '#1F2937' },
      { name: 'Phenol', percentage: 78, color: '#7C3AED' },
      { name: 'Aspirin', percentage: 16, color: '#0F766E' },
      { name: 'Metoprolol', percentage: 78, color: '#B45309' },
      { name: 'Simvastatin', percentage: 100, color: '#9333EA' }
    ];

    // Modify data based on patient
    switch (patientId) {
      case '44':
        return baseData.map(med => ({ ...med, percentage: Math.max(0, med.percentage - 40) }));
      case '45':
        return baseData.map(med => ({ ...med, percentage: Math.min(100, med.percentage + 20) }));
      case '48':
        return baseData.map(med => ({ ...med, percentage: Math.max(0, med.percentage - 60) }));
      default:
        return baseData;
    }
  };

  const medicationData = getMedicationData(patientId);

  const getReconciliationData = (patientId) => {
    const currentPatientId = parseInt(patientId) || 42;
    
    switch (currentPatientId) {
      case 44:
        return [
          { date: '8/28/2025', message: `Patient ${currentPatientId} missed the following medications: Atorvastatin, Metformin, Gabapentin.` },
          { date: '8/27/2025', message: `Patient ${currentPatientId} missed the following medications: Atorvastatin, Metformin.` },
          { date: '8/26/2025', message: `Patient ${currentPatientId} missed the following medications: All evening medications.` },
          { date: '8/25/2025', message: `Patient ${currentPatientId} device went offline for 12 hours.` }
        ];
      case 48:
        return [
          { date: '8/28/2025', message: `Patient ${currentPatientId} missed the following medications: Lisinopril, Aspirin.` },
          { date: '8/27/2025', message: `Patient ${currentPatientId} missed the following medications: Aspirin, Metoprolol.` },
          { date: '8/26/2025', message: `Patient ${currentPatientId} took medications 4 hours late.` }
        ];
      case 45:
        return [
          { date: '8/28/2025', message: `Patient ${currentPatientId} completed all medications on time.` },
          { date: '8/27/2025', message: `Patient ${currentPatientId} achieved 7-day medication streak.` }
        ];
      default:
        return [
          { date: '8/28/2025', message: `Patient ${currentPatientId} missed the following medications: Advil, Loratadine.` },
          { date: '8/27/2025', message: `Patient ${currentPatientId} missed the following medications: Advil, Loratadine.` },
          { date: '8/26/2025', message: `Patient ${currentPatientId} missed the following medications: Loratadine, Tylenol, Advil.` },
          { date: '8/25/2025', message: `Patient ${currentPatientId} missed the following medications: Loratadine, Tylenol, Advil.` }
        ];
    }
  };

  const reconciliationData = getReconciliationData(patientId);

  // Enhanced patient data for modal
  const patientData = {
    id: parseInt(patientId) || 42,
    name: `Patient ${patientId || 42}`,
    avatar: selectedPatient.id === 42 ? 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face&auto=format' :
            selectedPatient.id === 44 ? 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&auto=format' :
            selectedPatient.id === 57 ? 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=40&h=40&fit=crop&crop=face&auto=format' :
            'https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=40&h=40&fit=crop&crop=face&auto=format',
    status: selectedPatient.isOnline ? 'online' : 'offline',
    adherence: selectedPatient.id === 42 ? 85 : selectedPatient.id === 44 ? 45 : selectedPatient.id === 57 ? 84 : 67,
    lastDose: selectedPatient.id === 42 ? '2 hours ago' : selectedPatient.id === 44 ? '8 hours ago' : selectedPatient.id === 57 ? '3 hours ago' : '5 hours ago',
    isOnline: selectedPatient.isOnline,
    hasAlerts: selectedPatient.hasAlerts
  };

  return (
    <div className="dashboard patient-overview-style">
      {/* Chart Panel */}
      <div className="chart-panel">
        <div className="chart-header">
          <div className="chart-title-section">
            <div className="chart-title-row">
              <h2>
                {chartType === 'doseTime' ? 'Dose Administration Time' : 
                 chartType === 'adherence' ? 'Adherence by Medication' : 
                 chartType === 'videos' ? 'Patient Videos' :
                 chartType === 'chats' ? 'Patient Communications' :
                 chartType === 'notes' ? 'Clinical Notes' :
                 chartType === 'reconciliation' ? 'Medication Reconciliation' :
                 'Dashboard'}
              </h2>
            </div>
            <div className="chart-controls-row">
              <div className="chart-type-toggle">
                <button
                  className={`toggle-btn ${chartType === 'doseTime' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('doseTime')}
                >
                  <Clock size={16} />
                  Dose Time
                </button>
                <button
                  className={`toggle-btn ${chartType === 'adherence' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('adherence')}
                >
                  <TrendingUp size={16} />
                  Adherence
                </button>
                <button
                  className={`toggle-btn ${chartType === 'videos' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('videos')}
                >
                  <Play size={16} />
                  Videos
                </button>
                <button
                  className={`toggle-btn ${chartType === 'chats' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('chats')}
                >
                  <MessageCircle size={16} />
                  Chats
                </button>
                <button
                  className={`toggle-btn ${chartType === 'notes' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('notes')}
                >
                  <FileText size={16} />
                  Notes
                </button>
                <button
                  className={`toggle-btn ${chartType === 'reconciliation' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('reconciliation')}
                >
                  <ClipboardCheck size={16} />
                  Reconciliation
                </button>
              </div>
              
              {chartType === 'adherence' && (
                <div className="time-filter-dropdown-inline">
                  <select 
                    value={timeFilter} 
                    onChange={(e) => setTimeFilter(e.target.value)}
                    className="time-filter-select-inline"
                  >
                    <option value="3months">Trailing 3 months</option>
                    <option value="6months">Trailing 6 months</option>
                    <option value="1year">Trailing 1 year</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chart Content */}
        <div className="chart-content">
          {chartType === 'doseTime' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info */}
              <div className="patient-info-panel">
                  <div className="patient-info-header">
                    <div className="patient-avatar-section">
                      {patientData.avatar && (
                        <img src={patientData.avatar} alt={patientData.name} className="patient-avatar-large" />
                      )}
                      <div className="patient-details">
                        <h3>{patientData.name}</h3>
                        <span className="patient-id">ID: {patientData.id}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="patient-stats-list">
                    <div className="stat-row">
                      <span className="stat-label">STATUS:</span>
                      <span className={`status-badge ${patientData.status}`}>
                        {patientData.status === 'online' ? 'ONLINE' : 
                         patientData.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">ADHERENCE:</span>
                      <span className="adherence-value" style={{ color: getAdherenceColor(patientData.adherence) }}>
                        {patientData.adherence}%
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">LAST DOSE:</span>
                      <span className="stat-value">{patientData.lastDose}</span>
                    </div>
                  </div>
              </div>

              {/* Main Content Panel */}
              <div className="main-content-panel">
                <div className="dose-time-chart">
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={[
                      { time: -120, allPatients: 5, selectedPatient: 2 },
                      { time: -90, allPatients: 12, selectedPatient: 8 },
                      { time: -60, allPatients: 25, selectedPatient: 15 },
                      { time: -30, allPatients: 45, selectedPatient: 28 },
                      { time: -15, allPatients: 60, selectedPatient: 35 },
                      { time: 0, allPatients: 58, selectedPatient: 32 },
                      { time: 15, allPatients: 45, selectedPatient: 25 },
                      { time: 30, allPatients: 28, selectedPatient: 18 },
                      { time: 60, allPatients: 15, selectedPatient: 8 },
                      { time: 90, allPatients: 8, selectedPatient: 3 },
                      { time: 120, allPatients: 3, selectedPatient: 1 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis />
                      <Area type="monotone" dataKey="allPatients" stackId="1" stroke="#6b7280" fill="#9ca3af" />
                      <Area type="monotone" dataKey="selectedPatient" stackId="1" stroke="#f97316" fill="#fb923c" />
                      <Legend />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : chartType === 'adherence' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info */}
              <div className="patient-info-panel">
                <div className="patient-info-header">
                  <div className="patient-avatar-section">
                    {patientData.avatar && (
                      <img src={patientData.avatar} alt={patientData.name} className="patient-avatar-large" />
                    )}
                    <div className="patient-details">
                      <h3>{patientData.name}</h3>
                      <span className="patient-id">ID: {patientData.id}</span>
                    </div>
                  </div>
                </div>
                
                <div className="patient-stats-list">
                  <div className="stat-row">
                    <span className="stat-label">STATUS:</span>
                    <span className={`status-badge ${patientData.status}`}>
                      {patientData.status === 'online' ? 'ONLINE' : 
                       patientData.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">ADHERENCE:</span>
                    <span className="adherence-value" style={{ color: getAdherenceColor(patientData.adherence) }}>
                      {patientData.adherence}%
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">LAST DOSE:</span>
                    <span className="stat-value">{patientData.lastDose}</span>
                  </div>
                </div>
              </div>

              {/* Controls Panel - Medication List */}
              <div className="controls-panel">
                <div className="medication-controls-sidebar">
                  <div className="medication-control-header">
                    <div className="control-buttons">
                      <button 
                        className="control-btn"
                        onClick={() => setSelectedMedications(prev => 
                          Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: true }), {})
                        )}
                      >
                        Select All
                      </button>
                      <button 
                        className="control-btn"
                        onClick={() => setSelectedMedications(prev => 
                          Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {})
                        )}
                      >
                        Select None
                      </button>
                    </div>
                  </div>
                  
                  <div className="medication-list">
                    {Object.entries(selectedMedications).map(([medication, isSelected]) => (
                      <div 
                        key={medication} 
                        className={`medication-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleMedicationToggle(medication)}
                      >
                        <div className="medication-checkbox">
                          <div className={`checkbox ${isSelected ? 'checked' : ''}`}>
                            {isSelected && '✓'}
                          </div>
                        </div>
                        <div 
                          className="medication-color" 
                          style={{ backgroundColor: getMedicationColor(medication) }}
                        ></div>
                        <span className="medication-name">{medication}</span>
                        <span className="medication-percentage">{getMedicationPercentage(medication)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Main Content Panel - Chart */}
              <div className="main-content-panel">
                <div className="adherence-chart-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={[
                      { month: 'Mar', Atorvastatin: 85, Metformin: 78, Lisinopril: 92, Sertraline: 71, Levothyroxine: 89 },
                      { month: 'Apr', Atorvastatin: 82, Metformin: 74, Lisinopril: 88, Sertraline: 76, Levothyroxine: 91 },
                      { month: 'May', Atorvastatin: 88, Metformin: 79, Lisinopril: 85, Sertraline: 68, Levothyroxine: 87 },
                      { month: 'Jun', Atorvastatin: 91, Metformin: 81, Lisinopril: 89, Sertraline: 73, Levothyroxine: 93 },
                      { month: 'Jul', Atorvastatin: 87, Metformin: 77, Lisinopril: 91, Sertraline: 69, Levothyroxine: 88 },
                      { month: 'Aug', Atorvastatin: 84, Metformin: 75, Lisinopril: 86, Sertraline: 71, Levothyroxine: 90 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis domain={[50, 100]} />
                      {selectedMedications.Atorvastatin && <Line type="monotone" dataKey="Atorvastatin" stroke="#8B5CF6" strokeWidth={2} />}
                      {selectedMedications.Metformin && <Line type="monotone" dataKey="Metformin" stroke="#EF4444" strokeWidth={2} />}
                      {selectedMedications.Lisinopril && <Line type="monotone" dataKey="Lisinopril" stroke="#8B5CF6" strokeWidth={2} />}
                      {selectedMedications.Sertraline && <Line type="monotone" dataKey="Sertraline" stroke="#F59E0B" strokeWidth={2} />}
                      {selectedMedications.Levothyroxine && <Line type="monotone" dataKey="Levothyroxine" stroke="#EF4444" strokeWidth={2} />}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : chartType === 'videos' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info */}
              <div className="patient-info-panel">
                <div className="patient-info-header">
                  <div className="patient-avatar-section">
                    {patientData.avatar && (
                      <img src={patientData.avatar} alt={patientData.name} className="patient-avatar-large" />
                    )}
                    <div className="patient-details">
                      <h3>{patientData.name}</h3>
                      <span className="patient-id">ID: {patientData.id}</span>
                    </div>
                  </div>
                </div>
                
                <div className="patient-stats-list">
                  <div className="stat-row">
                    <span className="stat-label">STATUS:</span>
                    <span className={`status-badge ${patientData.status}`}>
                      {patientData.status === 'online' ? 'ONLINE' : 
                       patientData.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">ADHERENCE:</span>
                    <span className="adherence-value" style={{ color: getAdherenceColor(patientData.adherence) }}>
                      {patientData.adherence}%
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">LAST DOSE:</span>
                    <span className="stat-value">{patientData.lastDose}</span>
                  </div>
                </div>
              </div>

              {/* Main Content Panel - Videos */}
              <div className="main-content-panel">
                <div className="videos-container">
                  <div className="videos-header">
                    <h3>Videos for {patientData.name}</h3>
                  </div>
                  <div className="videos-grid">
                    {[
                      { id: 1, title: "Evening Medication Schedule", date: "Aug 28, 2025, 8:45 PM", duration: "2:45", status: "completed", medications: ["Tylenol", "Atorvastatin", "Advil", "Levothyroxine"], thumbnail: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=200&h=120&fit=crop&auto=format" },
                      { id: 2, title: "Morning Medication Schedule", date: "Aug 28, 2025, 8:30 AM", duration: "4:12", status: "missed", medications: ["Metformin", "Lisinopril"], thumbnail: "https://images.unsplash.com/photo-1584362917165-526a968579e8?w=200&h=120&fit=crop&auto=format" },
                      { id: 3, title: "Evening Medication Schedule", date: "Aug 27, 2025, 8:45 PM", duration: "1:58", status: "completed", medications: ["Tylenol", "Atorvastatin", "Advil"], thumbnail: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=200&h=120&fit=crop&auto=format" },
                      { id: 4, title: "Morning Medication Schedule", date: "Aug 27, 2025, 8:30 AM", duration: "3:22", status: "partial", medications: ["Metformin", "Lisinopril"], thumbnail: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=200&h=120&fit=crop&auto=format" }
                    ].map((video) => (
                      <div key={video.id} className="video-card" onClick={() => { setSelectedVideo(video); setVideoModalOpen(true); }}>
                        <div className="video-thumbnail">
                          <img src={video.thumbnail} alt={video.title} />
                          <div className="video-duration">{video.duration}</div>
                          <div className={`video-status-dot ${video.status}`}></div>
                          <div className="video-play-overlay">
                            <Play size={24} fill="white" />
                          </div>
                        </div>
                        <div className="video-info">
                          <h4>{video.title}</h4>
                          <p className="video-date">{video.date}</p>
                          <div className="video-medications">
                            {video.medications.map((med, idx) => (
                              <span key={idx} className="med-tag">{med}</span>
                            ))}
                          </div>
                          <span className={`video-status ${video.status}`}>
                            {video.status === 'completed' && '✓ Completed'}
                            {video.status === 'missed' && '✗ Missed'}
                            {video.status === 'partial' && '~ Partial'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : chartType === 'chats' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info */}
              <div className="patient-info-panel">
                <div className="patient-info-header">
                  <div className="patient-avatar-section">
                    {patientData.avatar && (
                      <img src={patientData.avatar} alt={patientData.name} className="patient-avatar-large" />
                    )}
                    <div className="patient-details">
                      <h3>{patientData.name}</h3>
                      <span className="patient-id">ID: {patientData.id}</span>
                    </div>
                  </div>
                </div>
                
                <div className="patient-stats-list">
                  <div className="stat-row">
                    <span className="stat-label">STATUS:</span>
                    <span className={`status-badge ${patientData.status}`}>
                      {patientData.status === 'online' ? 'ONLINE' : 
                       patientData.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">ADHERENCE:</span>
                    <span className="adherence-value" style={{ color: getAdherenceColor(patientData.adherence) }}>
                      {patientData.adherence}%
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">LAST DOSE:</span>
                    <span className="stat-value">{patientData.lastDose}</span>
                  </div>
                </div>
              </div>

              {/* Main Content Panel - Chat */}
              <div className="main-content-panel">
                <div className="chat-container">
                  <div className="chat-messages">
                    {[
                      { id: 1, sender: "doctor", message: "Hi there! I noticed you've missed a few doses this week. Is everything okay?", time: "10:30 AM", senderName: "Dr. Smith" },
                      { id: 2, sender: "patient", message: "Sorry doctor, I've been having trouble remembering to take them. The HiDo device helps but sometimes I'm away from home.", time: "11:45 AM", senderName: patientData.name },
                      { id: 3, sender: "doctor", message: "No worries! Let's set up additional reminders on your phone. Consistency is important for your treatment.", time: "12:15 PM", senderName: "Dr. Smith" },
                      { id: 4, sender: "patient", message: "I'll make sure to take them on time from now on.", time: "3:20 PM", senderName: patientData.name }
                    ].map((message) => (
                      <div key={message.id} className={`message ${message.sender === 'doctor' ? 'sent' : 'received'}`}>
                        <div className="message-content">
                          <div className="message-header">
                            <span className="sender-name">{message.senderName}</span>
                            <span className="message-time">{message.time}</span>
                          </div>
                          <p className="message-text">{message.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="chat-input-container">
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="chat-input"
                    />
                    <button className="send-button" disabled={!newMessage.trim()}>
                      <Send size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : chartType === 'notes' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info */}
              <div className="patient-info-panel">
                <div className="patient-info-header">
                  <div className="patient-avatar-section">
                    {patientData.avatar && (
                      <img src={patientData.avatar} alt={patientData.name} className="patient-avatar-large" />
                    )}
                    <div className="patient-details">
                      <h3>{patientData.name}</h3>
                      <span className="patient-id">ID: {patientData.id}</span>
                    </div>
                  </div>
                </div>
                
                <div className="patient-stats-list">
                  <div className="stat-row">
                    <span className="stat-label">STATUS:</span>
                    <span className={`status-badge ${patientData.status}`}>
                      {patientData.status === 'online' ? 'ONLINE' : 
                       patientData.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">ADHERENCE:</span>
                    <span className="adherence-value" style={{ color: getAdherenceColor(patientData.adherence) }}>
                      {patientData.adherence}%
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">LAST DOSE:</span>
                    <span className="stat-value">{patientData.lastDose}</span>
                  </div>
                </div>
              </div>

              {/* Controls Panel - Notes List */}
              <div className="controls-panel">
                <div className="notes-controls">
                  <div className="notes-header">
                    <button className="new-note-btn">
                      <Plus size={16} />
                      New Note
                    </button>
                  </div>
                  <div className="notes-search">
                    <Search size={16} className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search notes"
                      value={notesSearchTerm}
                      onChange={(e) => setNotesSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                  <div className="notes-list">
                    {[
                      { id: 1, title: "Patient Review Report", date: "Aug 28, 2025, 8:45 PM", author: "Thatiane Gellman", summary: "Summary of Findings", selected: selectedNoteId === 1 },
                      { id: 2, title: "Patient Review Report", date: "Aug 27, 2025, 2:30 PM", author: "Dr. Smith", summary: "Weekly Assessment", selected: selectedNoteId === 2 },
                      { id: 3, title: "Patient Review Report", date: "Aug 26, 2025, 10:15 AM", author: "Nurse Johnson", summary: "Medication Compliance Review", selected: selectedNoteId === 3 },
                      { id: 4, title: "Patient Review Report", date: "Aug 25, 2025, 4:20 PM", author: "Dr. Williams", summary: "Monthly Progress Note", selected: selectedNoteId === 4 }
                    ].map((note) => (
                      <div
                        key={note.id}
                        className={`note-item ${note.selected ? 'selected' : ''}`}
                        onClick={() => setSelectedNoteId(note.id)}
                      >
                        <h4>{note.title}</h4>
                        <p className="note-meta">By: {note.author}</p>
                        <p className="note-meta">Patient: {patientData.id}</p>
                        <p className="note-summary">{note.summary}</p>
                        <p className="note-date">{note.date}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Main Content Panel - Note Details */}
              <div className="main-content-panel">
                <div className="note-details">
                  <div className="note-header">
                    <h3>Patient Review Report Patient:{patientData.id}</h3>
                        <div className="note-actions">
                          <button className="note-action-btn">
                            <Trash2 size={16} />
                          </button>
                        </div>
                  </div>
                  <div className="note-meta-info">
                    <p><strong>Reviewed by:</strong> Thatiane Gellman</p>
                    <p><strong>Date:</strong> Aug 28, 2025, 8:45 PM</p>
                  </div>
                  <div className="note-content">
                    <h4>Summary of Findings</h4>
                    <p>1. Medication Dispensing Behavior: Over the past month, patient has dispensed medication on a regular basis using the HiDo device. On a couple occasions, the patient appeared to have skipped taking the medication, despite actions suggested otherwise. Specifically, the medication cup was empty, but there were indications that the pills were not ingested.</p>
                    <p>2. Additional Medication Consumption: The patient has been observed taking extra pills during the morning schedule, in addition to those dispensed by the HiDO device. Making a total of 9 pills taken during the morning period.</p>
                    <p>3. Observational Insights: When the patient takes their medication and drinks water from a clear water bottle, it provides greater visibility. This allows for more reliable monitoring to ensure the patient is consuming their medication effectively, with approximately 99% certainty of adherence when observed.</p>
                  </div>
                </div>
              </div>
            </div>
          ) : chartType === 'reconciliation' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info */}
              <div className="patient-info-panel">
                <div className="patient-info-header">
                  <div className="patient-avatar-section">
                    {patientData.avatar && (
                      <img src={patientData.avatar} alt={patientData.name} className="patient-avatar-large" />
                    )}
                    <div className="patient-details">
                      <h3>{patientData.name}</h3>
                      <span className="patient-id">ID: {patientData.id}</span>
                    </div>
                  </div>
                </div>
                
                <div className="patient-stats-list">
                  <div className="stat-row">
                    <span className="stat-label">STATUS:</span>
                    <span className={`status-badge ${patientData.status}`}>
                      {patientData.status === 'online' ? 'ONLINE' : 
                       patientData.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">ADHERENCE:</span>
                    <span className="adherence-value" style={{ color: getAdherenceColor(patientData.adherence) }}>
                      {patientData.adherence}%
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">LAST DOSE:</span>
                    <span className="stat-value">{patientData.lastDose}</span>
                  </div>
                </div>
              </div>

              {/* Main Content Panel - Reconciliation Table */}
              <div className="main-content-panel">
                <div className="reconciliation-container">
                  <div className="reconciliation-header">
                    <div className="reconciliation-controls">
                      <button className="control-btn">
                        <Columns size={16} />
                        Columns
                      </button>
                      <button className="control-btn">
                        <Filter size={16} />
                        Filters
                        <span className="filter-count">1</span>
                      </button>
                      <button className="control-btn">
                        <MoreHorizontal size={16} />
                        Density
                      </button>
                      <button className="control-btn">
                        <Download size={16} />
                        Export
                      </button>
                    </div>
                  </div>
                  <div className="reconciliation-table-container">
                    <table className="reconciliation-table">
                      <thead>
                        <tr>
                          <th>Group</th>
                          <th>ID</th>
                          <th>Schedule Time</th>
                          <th>Away</th>
                          <th>Ad Hoc</th>
                          <th>Medication Name</th>
                          <th>Status</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { group: 95243, id: 342137, scheduleTime: "9/22/2025 6:00 pm", away: "×", adHoc: "×", medication: "Advil", status: "MISSED", time: "" },
                          { group: 95243, id: 342136, scheduleTime: "9/22/2025 6:00 pm", away: "×", adHoc: "×", medication: "Tylenol", status: "MISSED", time: "" },
                          { group: 95243, id: 342135, scheduleTime: "9/22/2025 6:00 pm", away: "×", adHoc: "×", medication: "Loratadine", status: "MISSED", time: "" },
                          { group: 95214, id: 342069, scheduleTime: "9/22/2025 3:00 am", away: "×", adHoc: "×", medication: "Advil", status: "MISSED", time: "" },
                          { group: 95214, id: 342068, scheduleTime: "9/22/2025 3:00 am", away: "×", adHoc: "×", medication: "Tylenol", status: "MISSED", time: "" },
                          { group: 95214, id: 342067, scheduleTime: "9/22/2025 3:00 am", away: "×", adHoc: "×", medication: "Loratadine", status: "MISSED", time: "" },
                          { group: 95200, id: 342036, scheduleTime: "9/21/2025 6:00 pm", away: "×", adHoc: "×", medication: "Advil", status: "MISSED", time: "" },
                          { group: 95200, id: 342035, scheduleTime: "9/21/2025 6:00 pm", away: "×", adHoc: "×", medication: "Tylenol", status: "MISSED", time: "" },
                          { group: 95200, id: 342034, scheduleTime: "9/21/2025 6:00 pm", away: "×", adHoc: "×", medication: "Loratadine", status: "MISSED", time: "" }
                        ].map((row, index) => (
                          <tr key={index}>
                            <td>{row.group}</td>
                            <td>{row.id}</td>
                            <td>{row.scheduleTime}</td>
                            <td>{row.away}</td>
                            <td>{row.adHoc}</td>
                            <td>{row.medication}</td>
                            <td><span className="status-missed">{row.status}</span></td>
                            <td>{row.time}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="simple-message">
              <p>Chart type "{chartType}" content will be implemented here.</p>
              <p>Patient ID: {patientData.id}</p>
            </div>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {videoModalOpen && selectedVideo && (
        <div className="video-modal-overlay" onClick={() => setVideoModalOpen(false)}>
          <div className="video-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3>{selectedVideo.title}</h3>
              <button className="video-modal-close" onClick={() => setVideoModalOpen(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="video-modal-content">
              <div className="video-player-large">
                <img src={selectedVideo.thumbnail} alt={selectedVideo.title} style={{width: '100%', height: '400px', objectFit: 'cover'}} />
                <div className="video-play-button-large">
                  <Play size={48} fill="white" />
                </div>
              </div>
              <div className="video-modal-info">
                <p><strong>Date:</strong> {selectedVideo.date}</p>
                <p><strong>Duration:</strong> {selectedVideo.duration}</p>
                <p><strong>Status:</strong> {selectedVideo.status}</p>
                <div className="video-modal-medications">
                  <strong>Medications:</strong>
                  {selectedVideo.medications.map((med, idx) => (
                    <span key={idx} className="med-tag">{med}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
